import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { getAppTitle } from '../utils/config';
import { getContent, BreadcrumbItem } from '../content/i18n';
import { useLocale, useLocalizedPath } from '../hooks/useLocale';
import { localizePath } from '../config/seoPages';
import { ChevronRightIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

const ThemeToggleButton: React.FC = () => {
  const { toggleTheme, resolvedTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors shadow-sm"
      title="Toggle theme"
    >
      {resolvedTheme === 'dark' ? (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ) : (
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )}
    </button>
  );
};

interface PublicLayoutProps {
  children: React.ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  wide?: boolean;
}

export const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  breadcrumbs,
  wide = false,
}) => {
  const locale = useLocale();
  const localizedPath = useLocalizedPath();
  const content = getContent(locale);
  const { pathname } = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [pathname]);

  const resolveNavHref = (href: string) => localizedPath(href.replace(/^\/en/, '') || '/');

  const isNavActive = (href: string) => pathname === resolveNavHref(href);

  const navLinkClass = (active: boolean) =>
    `block px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active
        ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20'
        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800'
    }`;

  const alternateLocale = locale === 'zh' ? 'en' : 'zh';
  const alternatePath = localizePath(pathname, alternateLocale);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || mobileMenuOpen
            ? 'bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-700'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3 md:gap-8 min-w-0">
              <Link to={localizedPath('/')} className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white truncate">
                {getAppTitle()}
              </Link>
              <nav className="hidden md:flex items-center gap-1">
                {content.nav.map((item) => (
                  <Link
                    key={item.href}
                    to={resolveNavHref(item.href)}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isNavActive(item.href)
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-3 flex-shrink-0">
              <Link
                to={alternatePath}
                className="text-sm text-gray-600 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 px-2"
              >
                {locale === 'zh' ? 'EN' : '中文'}
              </Link>
              <ThemeToggleButton />
              <Link
                to={localizedPath('/login')}
                className="hidden sm:inline text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                {content.common.login}
              </Link>
              <Link
                to={localizedPath('/register')}
                className="hidden sm:inline-block bg-primary-600 hover:bg-primary-700 text-white px-3 sm:px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                {content.common.register}
              </Link>
              <button
                type="button"
                className="md:hidden p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen((open) => !open)}
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <nav className="md:hidden pb-4 border-t border-gray-200 dark:border-gray-700 pt-3">
              <div className="flex flex-col gap-1">
                {content.nav.map((item) => (
                  <Link
                    key={item.href}
                    to={resolveNavHref(item.href)}
                    className={navLinkClass(isNavActive(item.href))}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2 sm:hidden">
                  <Link
                    to={localizedPath('/login')}
                    className={navLinkClass(false)}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {content.common.login}
                  </Link>
                  <Link
                    to={localizedPath('/register')}
                    className="block text-center bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {content.common.register}
                  </Link>
                </div>
              </div>
            </nav>
          )}
        </div>
      </header>

      <main className="pt-16">
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <nav aria-label="Breadcrumb" className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              {breadcrumbs.map((crumb, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <ChevronRightIcon className="h-4 w-4 mx-1" />}
                  {crumb.href ? (
                    <Link to={crumb.href} className="hover:text-primary-600 dark:hover:text-primary-400">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="text-gray-900 dark:text-white">{crumb.label}</span>
                  )}
                </React.Fragment>
              ))}
            </nav>
          </div>
        )}
        <div className={wide ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>
          {children}
        </div>
      </main>

      <footer className="bg-gray-900 dark:bg-gray-950 text-white py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">{getAppTitle()}</h3>
              <p className="text-gray-400 text-sm">
                {locale === 'zh'
                  ? '专业跨境网络服务提供商，为跨境电商、TikTok 运营、海外 AI 工具及国际协作提供稳定可靠的全球业务网络。'
                  : 'Cross-border network service for e-commerce, TikTok operations, overseas AI tools, and global collaboration.'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4">{locale === 'zh' ? '产品' : 'Product'}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                {content.footer.product.map((item) => (
                  <li key={item.href}>
                    <Link to={localizedPath(item.href.replace(/^\/en/, '') || '/')} className="hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4">{locale === 'zh' ? '支持' : 'Support'}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                {content.footer.support.map((item) => (
                  <li key={item.label}>
                    <Link to={localizedPath(item.href.replace(/^\/en/, '') || '/')} className="hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold mb-4">{locale === 'zh' ? '公司' : 'Company'}</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                {content.footer.company.map((item) => (
                  <li key={item.href}>
                    <Link to={localizedPath(item.href.replace(/^\/en/, '') || '/')} className="hover:text-white transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} {getAppTitle()}. {locale === 'zh' ? '保留所有权利。' : 'All rights reserved.'}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
